// src/pages/EmergencyLanding.jsx
// Emergency roll call for H&S / fire safety. Live data from Firestore.

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { Download, ArrowLeft } from 'lucide-react';
import withProjectPermissions from '../components/withProjectPermissions';
import {
  collection,
  doc,
  getDoc,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase/firebase';

const fullNameFromUserData = (userData) => {
  if (!userData) return 'Unknown';
  const first = userData.firstName || userData.displayName?.split(' ')[0] || '';
  const last = userData.lastName || userData.displayName?.split(' ').slice(1).join(' ') || '';
  const full = [first, last].filter(Boolean).join(' ').trim();
  return full || 'Unknown';
};

function EmergencyLanding() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [onSite, setOnSite] = useState([]);
  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const projectSnap = await getDoc(doc(db, 'projects', projectId));
        const projectData = projectSnap.data();
        const name = projectData?.name || 'Project';
        const createdBy = projectData?.createdBy;
        const collaborators = projectData?.collaborators || {};

        if (cancelled) return;
        setProject({
          name,
          projectCode: projectData?.projectCode,
          address: projectData?.address,
          postcode: projectData?.postcode
        });

        // 1. Fetch siteAccess - who's on site
        const siteAccessSnap = await getDocs(
          collection(db, 'projects', projectId, 'siteAccess')
        );
        const onSiteDocs = siteAccessSnap.docs.filter(
          (d) => d.data().currentStatus === true
        );

        const onSiteList = [];
        for (const siteDoc of onSiteDocs) {
          const userId = siteDoc.id;
          const siteData = siteDoc.data();
          const lastActiveAt = siteData.lastActiveAt?.toDate?.()?.getTime?.() / 1000;

          const logsSnap = await getDocs(
            collection(db, 'projects', projectId, 'siteAccess', userId, 'logs')
          );
          const checkInLogs = logsSnap.docs
            .map((d) => ({ id: d.id, ...d.data() }))
            .filter((l) => l.type === 'checkIn')
            .sort((a, b) => (b.timestamp?.toMillis?.() ?? 0) - (a.timestamp?.toMillis?.() ?? 0));
          const logData = checkInLogs[0];
          const personName = logData?.name || 'Unknown';
          const ts = logData?.timestamp;
          const checkInTime = ts?.toDate?.()?.getTime?.() / 1000 ?? ts?.toMillis?.() / 1000 ?? Date.now() / 1000;

          onSiteList.push({
            userId,
            name: personName,
            checkInTime,
            lastActiveAt: lastActiveAt ?? checkInTime
          });
        }

        if (cancelled) return;
        setOnSite(onSiteList);

        // 2. Build emergency contacts (owner + pm_collaborators accepted)
        const userIdsToFetch = [];
        if (createdBy) userIdsToFetch.push(createdBy);
        for (const [uid, collab] of Object.entries(collaborators)) {
          if (collab?.status === 'accepted' && collab?.role === 'pm_collaborator') {
            userIdsToFetch.push(uid);
          }
        }

        const contacts = [];
        for (const uid of userIdsToFetch) {
          const userSnap = await getDoc(doc(db, 'users', uid));
          const userData = userSnap.data();
          const name = fullNameFromUserData(userData);
          const phone = userData?.phone || userData?.phoneNumber || '';
          const email = userData?.email || '';
          const role = uid === createdBy ? 'Project Manager' : 'Collaborator';
          contacts.push({ userId: uid, name, role, phone, email });
        }

        if (cancelled) return;
        setEmergencyContacts(contacts);
        setLastUpdated(new Date());
      } catch (err) {
        console.error('Error fetching emergency data:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => { cancelled = true; };
  }, [projectId]);

  const formatTime = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts * 1000);
    return d.toLocaleString('en-GB', {
      dateStyle: 'short',
      timeStyle: 'short'
    });
  };

  const exportPDF = () => {
    setIsExporting(true);
    try {
      const pdfDoc = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageHeight = 842;
      const margin = 40;
      let y = 40;

      const addPageIfNeeded = (requiredSpace = 60) => {
        if (y + requiredSpace > pageHeight - 40) {
          pdfDoc.addPage();
          y = 40;
        }
      };

      const projectName = project?.name || 'Project';

      // Title
      pdfDoc.setFontSize(18);
      pdfDoc.setFont('helvetica', 'bold');
      const titleLines = pdfDoc.splitTextToSize(`Roll Call - ${projectName}`, 515);
      for (const line of titleLines) {
        pdfDoc.text(line, margin, y);
        y += 22;
      }
      y += 6;

      // Generated timestamp
      pdfDoc.setFontSize(10);
      pdfDoc.setFont('helvetica', 'normal');
      pdfDoc.setTextColor(128, 128, 128);
      pdfDoc.text(`Generated: ${new Date().toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })}`, margin, y);
      pdfDoc.setTextColor(0, 0, 0);
      y += 24;

      // Disclaimer
      pdfDoc.setTextColor(249, 115, 22);
      pdfDoc.setFontSize(10);
      pdfDoc.text('For reference only. Verify at assembly point.', margin, y);
      pdfDoc.setTextColor(0, 0, 0);
      y += 24;

      // On site section
      pdfDoc.setFontSize(14);
      pdfDoc.setFont('helvetica', 'bold');
      const onSiteLabel = onSite.length === 1 ? '1 person' : `${onSite.length} people`;
      pdfDoc.text(`On site now (${onSiteLabel})`, margin, y);
      y += 20;

      pdfDoc.setFontSize(12);
      pdfDoc.setFont('helvetica', 'normal');
      for (const person of onSite) {
        addPageIfNeeded(50);
        pdfDoc.text(person.name, margin, y);
        y += 14;
        pdfDoc.setTextColor(128, 128, 128);
        pdfDoc.setFontSize(10);
        pdfDoc.text(`Checked in: ${formatTime(person.checkInTime)}`, margin + 12, y);
        y += 12;
        pdfDoc.text(`Last active: ${formatTime(person.lastActiveAt)}`, margin + 12, y);
        pdfDoc.setTextColor(0, 0, 0);
        pdfDoc.setFontSize(12);
        y += 16;
      }
      y += 16;

      // Emergency contacts section
      addPageIfNeeded(50);
      pdfDoc.setFontSize(14);
      pdfDoc.setFont('helvetica', 'bold');
      pdfDoc.text('Emergency contacts', margin, y);
      y += 20;

      pdfDoc.setFontSize(12);
      pdfDoc.setFont('helvetica', 'normal');
      for (const contact of emergencyContacts) {
        addPageIfNeeded(50);
        pdfDoc.text(contact.name, margin, y);
        pdfDoc.setTextColor(128, 128, 128);
        pdfDoc.setFontSize(10);
        pdfDoc.text(`(${contact.role})`, margin + 120, y);
        pdfDoc.setTextColor(0, 0, 0);
        pdfDoc.setFontSize(12);
        y += 16;
        if (contact.phone) {
          pdfDoc.setFontSize(10);
          pdfDoc.text(contact.phone, margin + 20, y);
          y += 14;
        }
        if (contact.email) {
          pdfDoc.text(contact.email, margin + 20, y);
          y += 14;
        }
        pdfDoc.setFontSize(12);
        y += 8;
      }

      // Footer
      pdfDoc.setFontSize(10);
      pdfDoc.setTextColor(128, 128, 128);
      pdfDoc.text('Generated by CrewChain', margin, pageHeight - 40);

      pdfDoc.save(`roll-call-${projectName.replace(/[^a-z0-9]/gi, '-')}.pdf`);
    } catch (err) {
      console.error('Error exporting PDF:', err);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center', backgroundColor: 'var(--branding-app-surface)', minHeight: '100vh' }}>
        <p>Loading emergency roll call...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: 40, backgroundColor: 'var(--branding-app-surface)', minHeight: '100vh' }}>
      {/* Back Button */}
      <div style={{ marginBottom: 24 }}>
        <button
          onClick={() => navigate(`/project/${projectId}`)}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--cc-text-muted)',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            fontWeight: '500',
            transition: 'color 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = 'var(--branding-app-primary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = 'var(--cc-text-muted)';
          }}
        >
          <ArrowLeft size={20} />
          Back to project
        </button>
      </div>

      {/* Header: Project info + Export */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 600, color: 'var(--cc-text-primary)', marginBottom: 24 }}>
            {project?.name || 'Project'}
          </h1>
          {project?.projectCode && (
            <p style={{ fontSize: 15, color: 'var(--cc-text-secondary)', marginBottom: 12 }}>
              <strong>Project Code:</strong> {project.projectCode}
            </p>
          )}
          <p style={{ fontSize: 16, color: 'var(--cc-text-secondary)', marginBottom: 16 }}>
            <strong>Address:</strong> {project?.address || '—'}
          </p>
          <p style={{ fontSize: 16, color: 'var(--cc-text-secondary)', marginBottom: 24 }}>
            <strong>Postcode:</strong> {project?.postcode || '—'}
          </p>
        </div>
        <button
          onClick={exportPDF}
          disabled={isExporting}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            background: 'var(--branding-app-primary)',
            color: '#fff',
            border: 'none',
            padding: '8px 16px',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 500,
            cursor: isExporting ? 'not-allowed' : 'pointer',
            opacity: isExporting ? 0.7 : 1
          }}
        >
          <Download size={18} />
          {isExporting ? 'Exporting...' : 'Export PDF'}
        </button>
      </div>

      {/* Disclaimer */}
      <div
        style={{
          padding: 16,
          background: 'rgba(249, 115, 22, 0.1)',
          borderRadius: 8,
          marginBottom: 24,
          border: '1px solid rgba(249, 115, 22, 0.3)'
        }}
      >
        <strong>For reference only</strong>
        <p style={{ margin: '4px 0 0 0', fontSize: 14, color: 'var(--cc-text-muted)' }}>
          Verify everyone at assembly point.
        </p>
      </div>

      {lastUpdated && (
        <p style={{ fontSize: 12, color: 'var(--branding-app-surface-panel-hint)', marginBottom: 24 }}>
          Last updated: {lastUpdated.toLocaleString()}
        </p>
      )}

      {/* Two-column: On site (center) + Emergency contacts (sidebar) */}
      <div style={{
        display: 'flex',
        gap: 24,
        maxWidth: 1200,
        margin: '0 auto',
        flexWrap: 'wrap'
      }}>
        {/* On site - main content */}
        <div style={{
          flex: 1,
          minWidth: 280,
          backgroundColor: 'var(--cc-card-bg)',
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          padding: 32
        }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--cc-text-primary)', marginBottom: 16, borderBottom: '1px solid var(--branding-app-surface-panel-border)', paddingBottom: 12 }}>
            On site now
          </h2>
          <p style={{ fontSize: 14, color: 'var(--cc-text-muted)', marginBottom: 16 }}>
            {onSite.length === 1 ? '1 person' : `${onSite.length} people`}
          </p>
          {onSite.length === 0 ? (
            <p style={{ color: 'var(--branding-app-surface-panel-hint)', padding: 16 }}>No one currently on site</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {onSite.map((person) => (
                <div
                  key={person.userId}
                  style={{
                    padding: 16,
                    background: 'var(--branding-app-surface-panel)',
                    borderRadius: 8,
                    border: '1px solid var(--branding-app-surface-panel-border)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#22c55e'
                      }}
                    />
                    <div>
                      <div style={{ fontWeight: 500 }}>{person.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--cc-text-muted)' }}>
                        Checked in: {formatTime(person.checkInTime)}
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--cc-text-muted)' }}>
                        Last active: {formatTime(person.lastActiveAt)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Emergency contacts - sidebar */}
        <div style={{
          width: 320,
          minWidth: 280,
          backgroundColor: 'var(--cc-card-bg)',
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
          padding: 32
        }}>
          <h2 style={{ fontSize: 20, fontWeight: 600, color: 'var(--cc-text-primary)', marginBottom: 16, borderBottom: '1px solid var(--branding-app-surface-panel-border)', paddingBottom: 12 }}>
            Emergency contacts
          </h2>
          <p style={{ fontSize: 14, color: 'var(--cc-text-muted)', marginBottom: 16 }}>
            {emergencyContacts.length === 1 ? '1 contact' : `${emergencyContacts.length} contacts`}
          </p>
          {emergencyContacts.length === 0 ? (
            <p style={{ color: 'var(--branding-app-surface-panel-hint)', padding: 16 }}>No contacts available</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {emergencyContacts.map((contact) => (
                <div
                  key={contact.userId}
                  style={{
                    padding: 16,
                    background: 'var(--branding-app-surface-panel)',
                    borderRadius: 8,
                    border: '1px solid var(--branding-app-surface-panel-border)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <strong>{contact.name}</strong>
                    <span
                      style={{
                        fontSize: 12,
                        padding: '2px 8px',
                        background: 'var(--cc-active-bg)',
                        borderRadius: 4,
                        color: 'var(--cc-text-muted)'
                      }}
                    >
                      {contact.role}
                    </span>
                  </div>
                  {contact.phone && (
                    <a
                      href={`tel:${contact.phone}`}
                      style={{ display: 'block', fontSize: 14, color: 'var(--branding-app-primary)', marginBottom: 4 }}
                    >
                      {contact.phone}
                    </a>
                  )}
                  {contact.email && (
                    <a
                      href={`mailto:${contact.email}`}
                      style={{ display: 'block', fontSize: 14, color: 'var(--branding-app-primary)' }}
                    >
                      {contact.email}
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default withProjectPermissions(EmergencyLanding, { requireEdit: false });
